import {getUserLevel} from '@netx/core-user-next/utils/getUserLevel';

/**
 * Manage plugins on a single view's instance
 * @class
 * @param {Netx.View} view View to store plugins on
 * @example
 *  {
 *      pluginUniqueNameForView: {
 *          name             : 'tooltip',     // The name of the plugin. Used for intializing
 *          dataAlias        : 'bs.tooltip',  // The name of the data the plugin stores on the element, if it differs from the name
 *          enablerTest      : function() {}, // Just like actions
 *          userLevel        : 8,             // Just like actions
 *          multiple         : true,          // Should we allow multiple instances of this plugin on the view (default is false),
 *          destroyMethod    : 'destroy',     // If the plugin's destroy method name differs from `destroy` - can also be a function (the instance will be the only parameter)
 *          initializeMethod : 'initialize',  // If the plugin's initialize method name differs from `initialize` - can also be a function (passes the plugin object, the $el, the init options, and a callback - the instance you create must be returned to this callback)
 *          updateMethod     : 'update',      // If the plugin's update method name differs from `update`
 *          elementKey       : 'container',   // If the plugin's element key differs from '$el'
 *          options          : {
 *              // Plugin options
 *              placement: 'top'
 *          }
 *      }
 *  }
 */
const extendPlugin = (plugin, opts) => {
	const pluginOptions = {...plugin.options, ...opts.options};
	Object.assign(plugin, opts);
	plugin.options = pluginOptions;
};

export default function (view) {
	/** @lends Netx.Plugins */
	var plugins = {},
		publicMethods = {};
	/**
	 * Extend modifications onto exisiting plugin
	 * @method
	 * @public
	 * @param {object} plugin An object of plugin(s)
	 * @param {object} opts Options (not used currently)
	 */
	function Extend(plugin, opts) {
		opts = opts || {};
		plugin = plugin || {};
		// Loop over passed plugins and extend to existing
		_.each(plugin, function (p, k) {
			if (!_.isUndefined(plugins[k])) {
				extendPlugin(plugins[k], p);
			} else {
				console.warn(
					'The plugin ' +
						k +
						' has not been defined. Please use `register` in place of `extend`.',
				);
			}
		});

		// Chain to public methods
		return publicMethods;
	}
	/**
	 * Find plugin(s) by name
	 * @method
	 * @public
	 * @param {array|string} name Plugin name (key, name, dataAlias)
	 * @return {array|boolean|object}
	 */
	function Find(name) {
		// Just to make things easier - assume array
		if (!_.isArray(name)) name = [name];

		var returnData = [];
		// Loop over plugin names
		_.each(name, function (n) {
			// It weas the key
			if (plugins[n]) {
				returnData.push(plugins[n]);
			}
			// It was not the key, try the name and alias
			else {
				_.each(plugins, function (plugin) {
					if (plugin.name === n || plugin.dataAlias === n) {
						returnData.push(plugin);
					}
				});
			}
		});
		// Just return false and not an empty array
		if (!returnData.length) {
			return false;
		}
		// If a single result, unwrap it from array
		return returnData.length > 1 ? returnData : returnData[0];
	}
	/**
	 * Get plugin(s) instance by name
	 * @method
	 * @public
	 * @param {array|string} name Plugin name (key, name, dataAlias)
	 * @param {element} $el Element to get plugin instance on
	 * @return {array|boolean|object}
	 */
	function GetInstance(name, $el) {
		var plugin = Find(name);
		if (plugin && plugin.instance) {
			if (_.isArray(plugin.instance)) {
				if ($el) {
					return _.filter(plugin.instance, function (instance) {
						var el = instance[plugin.elementKey || '$el'];
						if (el) {
							return el instanceof jQuery ? el.is($el) : el == $el;
						}
						return false;
					});
				}
			}
			return plugin.instance;
		}
		return false;
	}
	/**
	 * Register plugin(s)
	 * @method
	 * @public
	 * @param {object} plugin An object of plugin(s)
	 * @param {object} opts Options (not used currently)
	 */
	function Register(plugin, opts) {
		opts = opts || {};
		plugin = plugin || {};

		// Loop over passed plugins and add if not already defined
		_.each(plugin, function (p, k) {
			if (_.isUndefined(plugins[k])) {
				plugins[k] = p;
			} else {
				console.warn(
					'The plugin ' +
						k +
						' has already been defined. Please use `extend` in place of `register`.',
				);
			}
		});

		// Chain to public methods
		return publicMethods;
	}
	/**
	 * Remove plugin(s)
	 * @method
	 * @public
	 * @param {string} name Actual key name for plugin (the key, not the name/dataAlias)
	 */
	function Remove(name) {
		if (_.isArray(name)) {
			_.each(name, Remove);
		} else if (plugins[name]) {
			delete plugins[name];
		}

		// Chain to public methods
		return publicMethods;
	}

	/**
	 * Destroy plugin instance(s)
	 * @method
	 * @public
	 * @param {string} name Plugin name (key, name, dataAlias)
	 * @param {element} $el Element to destroy plugin on
	 */
	function Destroy(name, $el) {
		// Destroy them all if nothing was passed
		if (!arguments.length) {
			_.each(plugins, function (p) {
				destroyPlugin(p);
			});
		} else {
			// Multiple plugins to destroy
			if (_.isArray(name)) {
				_.each(name, function (n) {
					Destroy(n, $el);
				});
			}
			// Single plugin to destroy
			else {
				var plugin = Find(name);
				if (!plugin) {
					// console.warn( "The plugin " + name + " has not been defined. Cannot destroy what does not exist." );
				} else if (plugin.length) {
					_.each(plugin, function (p) {
						destroyPlugin(p, $el);
					});
				} else {
					destroyPlugin(plugin, $el);
				}
			}
		}

		// Chain to public methods
		return publicMethods;
	}
	/**
	 * Initialize plugin instance(s)
	 * @method
	 * @public
	 * @param {array|string} name Plugin name (key, name, dataAlias)
	 * @param {element} $el Element to initialize plugin on
	 * @param {object} opts Additional plugin options (support for passing custom options at init that may or may not be the same in the registered options)
	 * @return {promise}
	 */
	function Initialize(name, $el, opts) {
		var d = new $.Deferred(),
			promises = [];
		// Multiple plugins to init
		if (_.isArray(name)) {
			_.each(name, function (n) {
				var plugin = Find(n);
				if (!plugin) {
					console.warn(
						'The plugin ' +
							n +
							' has not been defined. Please use `register` prior to intializing.',
					);
				} else if (plugin.length) {
					_.each(
						plugin,
						function (p) {
							promises.push(initializePlugin(p, $el, opts));
						},
						this,
					);
				} else {
					promises.push(initializePlugin(plugin, $el, opts));
				}
			});
			$.when.apply($, promises).then(function (instances) {
				d.resolveWith(view, instances);
			});
			return d.promise();
		}

		// Single plugin to init
		var plugin = Find(name);
		if (!plugin) {
			console.warn(
				'The plugin ' +
					name +
					' has not been defined. Please use `register` prior to intializing.',
			);
		} else if (plugin.length) {
			_.each(
				plugin,
				function (p) {
					promises.push(initializePlugin(p, $el, opts));
				},
				this,
			);
			$.when.apply($, promises).then(function (instances) {
				d.resolveWith(view, instances);
			});
			return d.promise();
		} else {
			return initializePlugin(plugin, $el, opts);
		}
	}
	/**
	 * Update plugin(s) instance(s)
	 * @method
	 * @public
	 * @param {string} name Plugin name (key, name, dataAlias)
	 * @param {element} $el Element to update plugin on
	 * @param {object} opts Additional plugin update arguments
	 */
	function Update(name, $el, args) {
		var plugin = Find(name);
		if (!plugin) {
			console.warn(
				'The plugin ' +
					name +
					' has not been defined. Cannot update that which does not exist.',
			);
		} else if (plugin.length) {
			_.each(
				plugin,
				function (p) {
					updatePlugin(p, $el, args);
				},
				this,
			);
		} else {
			updatePlugin(plugin, $el, args);
		}

		// Chain to public methods
		return publicMethods;
	}

	/**
	 * Invoke a method on plugin(s) instance(s)
	 * @method
	 * @public
	 * @param {string} name Plugin name (key, name, dataAlias)
	 * @param {element} $el Element to update plugin on
	 * @param {string} methodName The method to invoke
	 * @param {object} opts Additional plugin update arguments
	 */
	function Invoke(name, $el, methodName, args) {
		// Redirect access to these methods
		switch (methodName) {
			case 'destroy':
				return Destroy(name, $el);
			case 'init':
			case 'initialize':
				return Initialize(name, $el, args);
			case 'update':
				return Update(name, $el, args);
		}

		var plugin = Find(name);
		if (!plugin) {
			console.warn(
				'The plugin ' +
					name +
					' has not been defined. Cannot invoke `' +
					methodName +
					'` that which does not exist.',
			);
		} else if (plugin.length) {
			_.each(
				plugin,
				function (p) {
					invokePluginMethod(p, $el, methodName, args);
				},
				this,
			);
		} else {
			invokePluginMethod(plugin, $el, methodName, args);
		}

		// Chain to public methods
		return publicMethods;
	}

	/**
	 * Destroy a plugin instance (if created)
	 * @method
	 * @private
	 * @param {object} plugin Plugin object (with options and instance etc)
	 * @param {element} $el Element to destroy plugin on
	 */
	function destroyPlugin(plugin, $el) {
		// Destroy helper
		function _destroy(instance) {
			if (instance) {
				// Allow for a user defined destroy method callback
				// The instance gets passed as the only argument to the callback
				if (plugin.destroyMethod && _.isFunction(plugin.destroyMethod)) {
					try {
						plugin.destroyMethod.call(view, instance);
					} catch (ex) {}
				}
				// Allow for a user defined destroy method string (the name of the method on the plugin differs from 'destroy')
				else if (
					plugin.destroyMethod &&
					_.isFunction(instance[plugin.destroyMethod])
				) {
					try {
						instance[plugin.destroyMethod]();
					} catch (ex) {}
				}
				// Classic destruction
				else if (_.isFunction(instance.destroy)) {
					try {
						// Some plugins do not play by the rules *cough* jQuery.sortable *cough*
						if (instance instanceof jQuery) {
							instance[plugin.name]('destroy');
						} else {
							instance.destroy();
						}
					} catch (ex) {}
				}
			}
		}

		// Have a plugin and it has an instance(s)
		if (plugin && plugin.instance) {
			// We want to destroy a specific instance
			if ($el && _.isArray(plugin.instance)) {
				// Get the index of our instance
				var idx = _.indexOf(
					plugin.instance,
					$el.data(plugin.dataAlias || plugin.name),
				);
				if (idx > -1) {
					_destroy(plugin.instance[idx]);
					plugin.instance.splice(idx, 1);
				}
			}
			// We want to destroy all instances
			else {
				// This has multiple instances
				if (_.isArray(plugin.instance)) {
					_.each(plugin.instance, _destroy);
				}
				// Just a single instance
				else {
					_destroy(plugin.instance);
				}
				delete plugin.instance;
			}
		} else {
			// Let them know there was no instance to destroy?
		}
	}
	/**
	 * Initialize a plugin instance
	 * This returns a promise in case there is anything async we need to do before the plugin can be initialized
	 * Upon successful initialization we (helpfully) pass back the new plugin instance
	 * @method
	 * @private
	 * @param {object}  plugin  Plugin object (with options and instance etc)
	 * @param {element} $el     Element to initialize plugin on
	 * @param {object}  options Instance specific options (additional options that will get applied on top of any previosuly defined global options for the plugin init)
	 *
	 * @return {array|object} Plugin instance(s)
	 */
	function initializePlugin(plugin, $el, options) {
		var d = new $.Deferred();
		// If an $el was not specified assume this view's $el
		$el = $($el) || view.$el;
		// Make sure we:
		// Have a plugin
		// And that plugin has a name
		// And that we have an element to initialize the plugin on
		if (plugin && plugin.name && $el.length) {
			// If an instance already exists, destroy it
			if (plugin.instance) {
				destroyPlugin(plugin, $el);
			}
			// If the plugin can have mutliple instances, `plugin.instance` needs to be an array
			if (plugin.multiple && !_.isArray(plugin.instance)) {
				plugin.instance = [];
			}

			// TODO: when we refactor actions to have more broken out methods we will not need to recreate the tests here
			// If user level is defined and the user level is not met - invalid
			var valid = !(
				!_.isUndefined(plugin.userLevel) && getUserLevel() < plugin.userLevel
			);
			// Check against any enablerTests
			if (valid && plugin.enablerTest) {
				var tests = _.isArray(plugin.enablerTest)
					? plugin.enablerTest
					: [plugin.enablerTest];
				_.each(
					tests,
					function (t) {
						if (valid && _.isFunction(t)) valid = t(view);
					},
					view,
				);
			}

			// If we are still good - initialize plugin
			if (valid) {
				var instances;
				// Allow for a user defined intialize method callback
				// We pass the plugin object, the element, options and our deferred
				// It is the responsibility of the custom initializeMethod:
				//   To store the instance (via push if mutliple)
				//   To resolve the deferred and pass back the new instance
				if (_.isFunction(plugin.initializeMethod)) {
					if (plugin.multiple) {
						var promises = [];

						instances = [];
						_.each($el, function (_el) {
							var p = new $.Deferred();
							promises.push(p);
							// We pass the plugin object
							plugin.initializeMethod.call(
								view,
								plugin,
								$(_el),
								options,
								function (instance) {
									instances.push(instance);
									p.resolve();
								},
							);
						});
						$.when.apply($, promises).then(function () {
							// Concat multiple instances
							plugin.instance = plugin.instance.concat(instances);
							// Resolve and pass instances back
							d.resolveWith(view, [instances]);
						});
					} else {
						plugin.initializeMethod.call(
							view,
							plugin,
							$el,
							options,
							function (instance) {
								plugin.instance = instance;
								d.resolveWith(view, [instance]);
							},
						);
					}
				}
				// Classic init
				else {
					// DAM-6666: S2 demands we update our templates all over creation, but at least now we get a warning.
					if (plugin.name == 'select2' && !$el.is('select')) {
						console.error(
							'applying select2 to non-SELECT element -- this will not work.',
						);
					}

					var pluginReturn;
					// Assume it some oddball plugin, like typeahead, that wants multiple parameters and not just a single object
					if (_.isArray(options)) {
						pluginReturn = $el[plugin.name].apply($el, options);
					} else {
						// Extend the passed options on top of the default options (defined vie `Register` or `Extend`)
						pluginReturn = $el[plugin.name](
							_.extend({}, plugin.options || {}, options || {}),
						);
					}

					if (plugin.multiple) {
						instances = _.map($el, function (_el) {
							var _$el = $(_el);
							// Fallback on the _el if data is not found
							return _$el.data(plugin.dataAlias || plugin.name) || _$el;
						});
						// Concat multiple instances
						plugin.instance = plugin.instance.concat(instances);
						// Resolve and pass instances back
						d.resolveWith(view, [instances]);
					} else {
						// Set single instance
						// Fallback on the return of the plugin initialize if data is not found
						plugin.instance =
							$el.data(plugin.dataAlias || plugin.name) || pluginReturn;
						d.resolveWith(view, [plugin.instance]);
					}
				}
			} else {
				// Invalid
				d.reject();
			}
		} else {
			// Let them know things are missing?
			d.reject();
		}
		return d.promise();
	}
	/**
	 * Invoke a plugin instance method (if created)
	 * @method
	 * @private
	 * @param {object} plugin Plugin object (with options and instance etc)
	 * @param {element} $el Element to invoke the plugin method on
	 * @param {mixed}  args Anything to pass to the update method
	 */
	function invokePluginMethod(plugin, $el, methodName, args) {
		// Invoke helper
		function _invoke(instance) {
			instance &&
				_.isFunction(instance[methodName]) &&
				instance[methodName].apply(instance, args);
		}

		if (plugin && plugin.instance) {
			// We want to update a specific instance
			if ($el) {
				if (_.isArray(plugin.instance)) {
					var idx = _.indexOf(
						plugin.instance,
						$el.data(plugin.dataAlias || plugin.name),
					);
					idx > -1 && _invoke(plugin.instance[idx]);
				} else {
					_invoke(plugin.instance);
				}
			} else {
				if (_.isArray(plugin.instance)) {
					_.each(plugin.instance, _invoke);
				} else {
					_invoke(plugin.instance);
				}
			}
		}
	}
	/**
	 * Update a plugin instance (if created)
	 * @method
	 * @private
	 * @param {object} plugin Plugin object (with options and instance etc)
	 * @param {element} $el Element to update the plugin on
	 * @param {mixed} args Anything to pass to the update method
	 */
	function updatePlugin(plugin, $el, args) {
		// Update helper
		function _update(instance) {
			if (instance) {
				// Support for custom update method name
				if (
					plugin.updateMethod &&
					_.isFunction(instance[plugin.updateMethod])
				) {
					instance[plugin.updateMethod].apply(instance, args);
				}
				// Default update method
				else if (_.isFunction(instance.update)) {
					instance.update.apply(instance, args);
				}
			}
		}

		var instance, idx;
		if (plugin && plugin.instance) {
			// We want to update an instance on a specific $el
			if ($el) {
				// An array of instances (multiple)
				if (_.isArray(plugin.instance)) {
					// Find specific instance of $el plugin in instances
					idx = _.indexOf(
						plugin.instance,
						$el.data(plugin.dataAlias || plugin.name),
					);
					idx > -1 && _update(plugin.instance[idx]);
				}
				// Single instance
				else {
					_update(plugin.instance);
				}
			}
			// We want tu update the instance regardless of $el
			else {
				// An array of instances (multiple)
				if (_.isArray(plugin.instance)) {
					_.each(plugin.instance, _update);
				}
				// Single instance
				else {
					_update(plugin.instance);
				}
			}
		}
	}

	// Our public methods
	publicMethods = {
		Extend: Extend,
		Find: Find,
		GetInstance: GetInstance,
		Register: Register,
		Remove: Remove,
		// Methods on the plugin themselves
		Initialize: Initialize,
		Invoke: Invoke,
		Destroy: Destroy,
		Update: Update,
		// Helper
		getAll() {
			return plugins;
		},
	};

	return publicMethods;
}
